/**
 * TanStack Query hooks for Knowledge Base document operations.
 * Types live in @/types/kb — this file is mutations + queries only.
 *
 * Non-upload hooks go through the shared `api` helper so they inherit JWT
 * injection and uniform error handling. Upload uses raw fetch because the
 * endpoint is multipart/form-data (api helper defaults to JSON).
 *
 * FR-073, FR-080, FR-082
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { api, ApiError } from '@/lib/api';
import type {
  KBDocumentResponse,
  KBDocumentUploadResponse,
  KBVersionHistoryResponse,
  KBSearchResponse,
  KBDocumentFilters,
  KBUploadPayload,
} from '@/types/kb';

export type { KBDocumentResponse, KBDocumentUploadResponse, KBVersionHistoryResponse, KBSearchResponse, KBDocumentFilters, KBUploadPayload };
export type { DocType, IndexingStatus } from '@/types/kb';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

// ─── Query keys ──────────────────────────────────────────────────────────────

export const kbKeys = {
  all: ['kb'] as const,
  list: (filters: KBDocumentFilters) => ['kb', 'list', filters] as const,
  versions: (docId: string) => ['kb', 'versions', docId] as const,
};

// The backend returns a paginated envelope for GET /kb/documents. Accept
// either that or a bare array so the hook survives either response shape.
type DocumentListBody =
  | KBDocumentResponse[]
  | { data: KBDocumentResponse[]; meta?: unknown };

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function useKBDocuments(filters: KBDocumentFilters = {}) {
  return useQuery({
    queryKey: kbKeys.list(filters),
    queryFn: async (): Promise<KBDocumentResponse[]> => {
      const params = new URLSearchParams();
      if (filters.doc_type) params.set('doc_type', filters.doc_type);
      if (filters.jurisdiction) params.set('jurisdiction', filters.jurisdiction);
      const qs = params.toString();
      const body = await api.get<DocumentListBody>(
        `/api/v1/kb/documents${qs ? `?${qs}` : ''}`,
      );
      const docs = Array.isArray(body) ? body : body.data;
      return docs.map((d) => ({
        ...d,
        indexing_status: d.indexed_at ? 'indexed' : 'indexing',
      }));
    },
  });
}

export function useKBVersionHistory(docId: string | null) {
  return useQuery({
    queryKey: kbKeys.versions(docId ?? ''),
    enabled: !!docId,
    queryFn: () =>
      api.get<KBVersionHistoryResponse>(`/api/v1/kb/documents/${docId ?? ''}/versions`),
  });
}

export function useKBUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: KBUploadPayload): Promise<KBDocumentUploadResponse> => {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      const form = new FormData();
      form.append('file', payload.file);
      form.append('title', payload.title);
      form.append('source', payload.source);
      form.append('doc_type', payload.doc_type);
      if (payload.jurisdiction) form.append('jurisdiction', payload.jurisdiction);
      if (payload.effective_date) form.append('effective_date', payload.effective_date);
      const res = await fetch(`${API_BASE}/api/v1/kb/documents`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: form,
      });
      if (!res.ok) throw new ApiError(res.status, await res.text());
      return res.json() as Promise<KBDocumentUploadResponse>;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: kbKeys.all });
      toast.success(`Document uploaded (version ${data.version}). Indexing will complete shortly.`);
    },
    onError: () => {
      toast.error('Upload failed. Please try again.');
    },
  });
}

export function useKBDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (docId: string) =>
      api.delete<unknown>(`/api/v1/kb/documents/${docId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: kbKeys.all });
      toast.success('Document deleted.');
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError && err.status === 404
          ? 'Document not found. It may have already been deleted.'
          : 'Delete failed. Please try again.';
      toast.error(msg);
    },
  });
}

export function useKBSearch() {
  return useMutation({
    mutationFn: (params: { query: string; jurisdiction?: string; top_k?: number }) =>
      api.post<KBSearchResponse>('/api/v1/kb/search', {
        query: params.query,
        jurisdiction: params.jurisdiction,
        top_k: params.top_k ?? 5,
      }),
  });
}
